import { useEffect, useRef, useState } from 'react';

// Mobile data plans and reduced-motion settings shouldn't pay for
// background autoplay. Save-Data + prefers-reduced-motion fall back
// to the poster image; otherwise an IntersectionObserver pauses any
// clip that scrolls more than ~70% out of view.
function autoplayBlocked() {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return true;
  } catch {
    /* unsupported — proceed */
  }
  const c = typeof navigator !== 'undefined' ? navigator.connection : null;
  if (c?.saveData) return true;
  return false;
}

export function AutoVideo({
  src,
  poster,
  className,
  style,
  alt = '',
  onClick,
  ariaLabel,
  fallback = null,
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const blocked = autoplayBlocked();

  useEffect(() => {
    if (blocked) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        setVisible(entry.isIntersecting && entry.intersectionRatio > 0.25);
      },
      { threshold: [0, 0.25, 0.5, 0.75] }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [blocked, src]);

  useEffect(() => {
    // `src` must be a dep here, not just `visible`. When a parent
    // swaps the video (e.g. user uploads a new cover_video_id and
    // refreshMedia re-renders the same gallery slot with a new src),
    // `visible` typically stays true, so without `src` in the deps
    // the play() call would never re-fire — the new source would
    // load but sit paused on the poster until the user scrolled it
    // out and back in.
    const v = ref.current;
    if (!v) return;
    if (visible) {
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else {
      v.pause();
    }
  }, [visible, src]);

  if (blocked) {
    if (poster) {
      return (
        <img
          src={poster}
          alt={alt}
          className={className}
          style={style}
          loading="lazy"
          onClick={onClick}
          aria-label={ariaLabel}
        />
      );
    }
    // No poster + Save-Data / reduced-motion on. A `null` render here
    // produced invisible gallery items (the × delete and badge sat
    // on nothing). If a fallback element was provided, use it;
    // otherwise render an empty-state placeholder div that at least
    // occupies the layout slot with the styles the caller passed.
    if (fallback) return fallback;
    return (
      <div
        className={className}
        style={{ ...style, background: 'rgba(125, 211, 255, 0.06)' }}
        onClick={onClick}
        aria-label={ariaLabel || 'Video unavailable (data saver / reduced motion)'}
      />
    );
  }

  // Without a poster the <video> element paints solid black until the
  // first frame decodes — which on Home/Browse looked like a broken
  // thumbnail when a resident had a clip but no image. Use preload="auto"
  // when there's no poster so the first frame is fetched up front, and
  // hint the browser to treat early frames as the placeholder via
  // poster=""+ a background style so emoji/colours behind any
  // transparency stays visible while we wait.
  const noPoster = !poster;
  return (
    <video
      ref={ref}
      src={src}
      poster={poster || undefined}
      muted
      loop
      playsInline
      preload={noPoster ? 'auto' : 'metadata'}
      className={className}
      style={{
        ...style,
        backgroundColor: noPoster ? 'rgba(125, 211, 255, 0.06)' : style?.backgroundColor,
      }}
      onClick={onClick}
      aria-label={ariaLabel}
    />
  );
}
